import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from "react";
import { MessageItem } from "./components";
import { transformApiMessage } from "./types";
import { useAuth } from "@/context/AuthContext";
import { getUserFullName } from "@/utils/formatDisplayName";
import { messagesService, } from "@/api/services/messages.service";
import { useMessagesSignalR } from "@/hooks/useMessagesSignalR";
import { useToast } from "@/components/common";
import styles from "./ContractorMessagesPage.module.css";
/** Normalize API response: backend may return array or { data: [] } */
function toThreadList(response) {
    const d = response?.data;
    if (Array.isArray(d))
        return d;
    if (typeof d === "object" &&
        d !== null &&
        "data" in d &&
        Array.isArray(d.data)) {
        return d.data;
    }
    return [];
}
/** Normalize getMessages response: array or { data: [] } */
function toMessageList(raw) {
    if (Array.isArray(raw))
        return raw;
    if (typeof raw === "object" &&
        raw !== null &&
        "data" in raw &&
        Array.isArray(raw.data)) {
        return raw.data;
    }
    return [];
}
const LoadingSpinner = () => (_jsxs("div", { className: styles.loadingContainer, children: [_jsx("div", { className: styles.spinner }), _jsx("p", { children: "Loading messages..." })] }));
const STAFF_TITLE = "The Defit Group";
const STAFF_SUBTITLE = "DEFIT Staff";
export const ContractorMessagesPage = () => {
    const { user } = useAuth();
    const { info: showInfo, error: showError } = useToast();
    const [messages, setMessages] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [newMessageContent, setNewMessageContent] = useState("");
    const [isSendingNew, setIsSendingNew] = useState(false);
    const [generalThreadId, setGeneralThreadId] = useState(null);
    // The contractor's user.id is the contactId
    const contactId = user?.id;
    // Fetch messages for the contractor
    const fetchMessages = useCallback(async () => {
        if (!contactId) {
            setError("No contractor ID found. Please log in again.");
            setIsLoading(false);
            setGeneralThreadId(null);
            return;
        }
        try {
            setIsLoading(true);
            setError(null);
            // Get all threads for this contractor
            const threadsResponse = await messagesService.getThreadsByContact(contactId);
            const threads = toThreadList(threadsResponse);
            const generalId = threads.find((t) => (t.type ?? "").toLowerCase() === "general")?.id ??
                threads[0]?.id ??
                null;
            setGeneralThreadId(generalId);
            // Collect all messages from all threads
            const allMessages = [];
            // For each thread, get the messages via getMessages endpoint
            for (const thread of threads) {
                try {
                    const messagesResponse = await messagesService.getMessages(thread.id);
                    const threadMessages = toMessageList(messagesResponse.data);
                    // Transform and add messages
                    threadMessages.forEach((apiMsg) => {
                        allMessages.push(transformApiMessage(apiMsg));
                    });
                    // Mark thread as read
                    messagesService.markAsRead(thread.id).catch(console.error);
                }
                catch (err) {
                    console.error(`Failed to fetch messages for thread ${thread.id}:`, err);
                }
            }
            // Sort messages by timestamp (oldest first for display)
            allMessages.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
            setMessages(allMessages);
        }
        catch (err) {
            const status = err?.response
                ?.status;
            if (status === 404 || status === 204) {
                // No threads yet - contractor can still start a conversation via "Send to Staff"
                setMessages([]);
                setError(null);
                setGeneralThreadId(null);
            }
            else {
                console.error("Failed to fetch messages:", err);
                setError("Failed to load messages. Please try again.");
                setGeneralThreadId(null);
            }
        }
        finally {
            setIsLoading(false);
        }
    }, [contactId]);
    useMessagesSignalR(generalThreadId, {
        onNewMessage: (payload) => {
            fetchMessages();
            if (payload.message.senderId !== user?.id) {
                showInfo("New message received");
            }
        },
        onConnectionError: () => {
            showError("Real-time updates unavailable");
        },
    });
    useEffect(() => {
        fetchMessages();
    }, [fetchMessages]);
    const handleSendToStaff = async () => {
        if (!contactId || !user || !newMessageContent.trim())
            return;
        setIsSendingNew(true);
        setError(null);
        try {
            await messagesService.sendMessage({
                threadType: "general",
                contactId,
                assignedcontactId: contactId,
                content: newMessageContent.trim(),
                senderId: user.id,
                senderName: getUserFullName(user, "You"),
            });
            setNewMessageContent("");
            await fetchMessages();
        }
        catch (err) {
            console.error("Failed to send message to staff:", err);
            setError("Failed to send message. Please try again.");
        }
        finally {
            setIsSendingNew(false);
        }
    };
    // Reply function commented out - messages display in reply style only
    // const handleReply = async (messageId: string, content: string) => {
    //   if (!contactId || !user) {
    //     setError("Please log in to send messages.");
    //     return;
    //   }
    //   const parentMessage = messages.find((msg) => msg.id === messageId);
    //   if (!parentMessage) {
    //     setError("Could not find the message to reply to.");
    //     return;
    //   }
    //   try {
    //     setIsSending(true);
    //     setError(null);
    //     const response = await messagesService.sendMessage({
    //       threadId: parentMessage.threadId,
    //       content,
    //       parentId: messageId,
    //       contactId,
    //       senderId: user.id,
    //       senderName: user.name || user.email || "You",
    //     });
    //     const newReply: ConversationMessage = {
    //       id: response.data.id,
    //       content: response.data.content,
    //       sender: {
    //         id: response.data.senderId,
    //         name: response.data.senderName || user.name || "You",
    //         role: (response.data.senderType as "contractor") || "contractor",
    //       },
    //       timestamp: response.data.createdAt,
    //       isRead: true,
    //       threadId: response.data.threadId,
    //     };
    //     setMessages((prevMessages) =>
    //       prevMessages.map((msg) =>
    //         msg.id === messageId
    //           ? { ...msg, replies: [...(msg.replies || []), newReply] }
    //           : msg,
    //       ),
    //     );
    //   } catch (err) {
    //     console.error("Failed to send reply:", err);
    //     setError("Failed to send reply. Please try again.");
    //   } finally {
    //     setIsSending(false);
    //   }
    // };
    const hasMessages = messages.length > 0;
    if (isLoading) {
        return (_jsxs("div", { className: styles.container, children: [_jsx("h2", { className: styles.title, children: "Messages" }), _jsx(LoadingSpinner, {})] }));
    }
    return (_jsxs("div", { className: styles.container, children: [_jsx("h2", { className: styles.title, children: "Messages" }), error && (_jsxs("div", { className: styles.errorBanner, children: [_jsx("p", { children: error }), _jsx("button", { onClick: fetchMessages, children: "Retry" })] })), _jsxs("div", { className: styles.content, children: [_jsxs("div", { className: styles.messageStaffSection, children: [_jsxs("div", { className: styles.messageStaffHeader, children: [_jsx("h3", { className: styles.messageStaffTitle, children: STAFF_TITLE }), _jsx("p", { className: styles.messageStaffSubtitle, children: STAFF_SUBTITLE })] }), _jsxs("div", { className: styles.messageStaffCompose, children: [_jsx("textarea", { className: styles.messageStaffInput, placeholder: "Type your message to staff...", value: newMessageContent, onChange: (e) => setNewMessageContent(e.target.value), onKeyDown: (e) => {
                                            if (e.key === "Enter" && !e.shiftKey) {
                                                e.preventDefault();
                                                handleSendToStaff();
                                            }
                                        }, rows: 3, disabled: isSendingNew }), _jsx("button", { type: "button", className: styles.messageStaffButton, onClick: handleSendToStaff, disabled: !newMessageContent.trim() || isSendingNew, children: isSendingNew ? "Sending…" : "Send to Staff" })] })] }), hasMessages ? (_jsx("div", { className: styles.messagesContainer, children: messages.map((message) => (_jsx(MessageItem, { message: message, displayAsReply: true }, message.id))) })) : (_jsxs("div", { className: styles.emptyState, children: [_jsx("div", { className: styles.emptyIcon, children: "\uD83D\uDCAC" }), _jsx("h3", { className: styles.emptyTitle, children: "No Messages Yet" }), _jsx("p", { className: styles.emptyText, children: "Use the form above to message staff and begin a conversation." })] }))] })] }));
};
